import { useEffect, useState } from 'react';
import type { AssignmentForUser, ItemForUser, LoginMethod, SecretPayload } from '@shared/types';
import { EMPTY_SECRET, LOGIN_METHOD_HINT, LOGIN_METHOD_LABEL, hasOwnPassword } from '@shared/types';
import { useVault } from '../lib/vault';
import { estimateBits, generatePassword } from '../lib/crypto';
import { CopyButton, ErrorNote, Modal, useAction } from './ui';

/**
 * Ein Platz innerhalb eines Eintrags: ein Adobe-Zugang, eine Seriennummer.
 *
 * Das eigene Geheimnis ist freiwillig. Bei Adobe genuegt vielen die Information,
 * *wer* einen Platz hat - das persoenliche Passwort behaelt jede Person fuer
 * sich. Bei DaVinci ist die Seriennummer dagegen genau das, was hier hingehoert.
 */
/**
 * `secretOnly` ist der Fall "mein eigener Zugang": dann geht es allein um das
 * Passwort. Bezeichnung, Zuweisung und Obergrenze bleiben aussen vor - der
 * Server weist eine Aenderung daran ohnehin ab, und ein Feld anzuzeigen, das
 * nicht gespeichert werden darf, waere die schlechtere Loesung.
 */
export function AssignmentEditor({
  item,
  assignment,
  secretOnly = false,
  onClose,
}: {
  item: ItemForUser;
  assignment: AssignmentForUser | null;
  secretOnly?: boolean;
  onClose: () => void;
}): JSX.Element {
  const { users, me, readAssignmentSecret, saveAssignment } = useVault();
  const { busy, error, setError, run } = useAction();

  const isLicense = item.kind === 'license';
  const nextNumber = item.assignments.length + 1;

  const [label, setLabel] = useState(
    assignment?.label ?? (isLicense ? `Lizenz ${nextNumber}` : `Platz ${nextNumber}`),
  );
  const [assigneeUids, setAssigneeUids] = useState<string[]>(assignment?.assigneeUids ?? []);
  const [maxUsers, setMaxUsers] = useState<string>(
    assignment?.maxUsers === null || assignment?.maxUsers === undefined ? '' : String(assignment.maxUsers),
  );
  const [openToAll, setOpenToAll] = useState(assignment?.openToAll ?? false);
  const [device, setDevice] = useState(assignment?.device ?? '');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(assignment?.loginMethod ?? 'unknown');
  const [withSecret, setWithSecret] = useState(Boolean(assignment?.secret));
  const [secret, setSecret] = useState<SecretPayload>(EMPTY_SECRET);
  const [showSecret, setShowSecret] = useState(!assignment);
  const [loading, setLoading] = useState(Boolean(assignment?.secret));

  useEffect(() => {
    let cancelled = false;
    if (!assignment?.secret) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    readAssignmentSecret(item, assignment)
      .then((payload) => {
        if (!cancelled) setSecret(payload);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item, assignment, readAssignmentSecret, setError]);

  const active = users.filter((u) => u.status === 'active');
  const chosen = users.filter((u) => assigneeUids.includes(u.uid));
  const ohneSchluessel = chosen.filter((u) => !u.publicKey);
  const grenze = maxUsers.trim() === '' ? null : Number(maxUsers);
  const ueberbelegt = !openToAll && grenze !== null && assigneeUids.length > grenze;

  const toggle = (uid: string): void =>
    setAssigneeUids((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));

  async function submit(): Promise<void> {
    if (withSecret && !openToAll && ohneSchluessel.length > 0) {
      setError(
        `${ohneSchluessel.map((u) => u.name).join(', ')} ${ohneSchluessel.length === 1 ? 'hat' : 'haben'} noch keine Master-Passphrase gesetzt und könnte das Geheimnis nicht lesen. Entweder ohne Geheimnis speichern oder warten, bis die Einrichtung erledigt ist.`,
      );
      return;
    }
    const ok = await run(async () => {
      await saveAssignment({
        item,
        assignmentId: assignment?.id ?? null,
        label: label.trim(),
        assigneeUids,
        maxUsers: grenze,
        openToAll,
        loginMethod,
        device: device.trim(),
        secret: withSecret ? secret : null,
      });
      return true;
    });
    if (ok) onClose();
  }

  if (loading) {
    return (
      <Modal title="Wird geladen …" onClose={onClose}>
        <p className="muted small">Platz wird entschlüsselt.</p>
      </Modal>
    );
  }

  return (
    <Modal
      title={assignment ? 'Platz bearbeiten' : isLicense ? 'Lizenz hinzufügen' : 'Platz hinzufügen'}
      subtitle={item.title}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button className="btn-primary" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Speichern …' : 'Speichern'}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />

      {secretOnly ? (
        <p className="small muted" style={{ marginTop: 0 }}>
          Dein persönlicher Zugang bei <strong>{item.title}</strong>. Hier hinterlegst du dein
          eigenes Passwort – verschlüsselt, wie alles andere auch.
        </p>
      ) : null}

      {/* Steht bewusst vor allem anderen und auch im Modus "nur mein
          Passwort": davon haengt ab, ob es ueberhaupt eins gibt. */}
      <div className="field">
        <label htmlFor="a-login">Wie meldet man sich an?</label>
        <select
          id="a-login"
          value={loginMethod}
          onChange={(e) => setLoginMethod(e.target.value as LoginMethod)}
        >
          {(Object.keys(LOGIN_METHOD_LABEL) as LoginMethod[]).map((k) => (
            <option key={k} value={k}>
              {LOGIN_METHOD_LABEL[k]}
            </option>
          ))}
        </select>
        {LOGIN_METHOD_HINT[loginMethod] ? (
          <p className="small muted" style={{ margin: '5px 0 0' }}>
            {LOGIN_METHOD_HINT[loginMethod]}
          </p>
        ) : null}
      </div>

      {secretOnly ? null : (
      <>
      <div className="field-row">
        <div className="field">
          <label htmlFor="a-label">Bezeichnung</label>
          <input
            id="a-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={isLicense ? 'Lizenz 1' : 'Platz 1'}
            autoFocus
          />
        </div>
        <div className="field">
          <label htmlFor="a-max">
            {isLicense ? 'Personen pro Lizenz' : 'Personen pro Platz'}
          </label>
          <input
            id="a-max"
            type="number"
            min={1}
            value={maxUsers}
            onChange={(e) => setMaxUsers(e.target.value)}
            placeholder="unbegrenzt"
            disabled={openToAll}
          />
        </div>
      </div>

      <label className="check" style={{ marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={openToAll}
          onChange={(e) => setOpenToAll(e.target.checked)}
        />
        Das ganze Team darf sie nutzen
      </label>

      {openToAll ? (
        <p className="small muted" style={{ marginTop: -6, marginBottom: 14 }}>
          Alle im Team sehen {isLicense ? 'die Seriennummer' : 'das Passwort'} – auch wer später
          dazukommt, sobald du hier einmal neu speicherst.
        </p>
      ) : (
        <div className="field">
          <label>
            Zugewiesen an
            {grenze !== null ? (
              <span className={ueberbelegt ? 'badge badge-danger' : 'badge'} style={{ marginLeft: 8 }}>
                {assigneeUids.length} von {grenze}
              </span>
            ) : assigneeUids.length > 0 ? (
              <span className="badge" style={{ marginLeft: 8 }}>{assigneeUids.length}</span>
            ) : null}
          </label>
          <div className="stack">
            {active.map((u) => (
              <label key={u.uid} className="check">
                <input
                  type="checkbox"
                  checked={assigneeUids.includes(u.uid)}
                  onChange={() => toggle(u.uid)}
                />
                {u.name}
                <span className="muted small">{u.email}</span>
                {!u.publicKey ? (
                  <span className="badge badge-warn">noch keine Passphrase</span>
                ) : null}
              </label>
            ))}
          </div>
          {ueberbelegt ? (
            <div className="note note-danger" style={{ marginTop: 10 }}>
              Mehr Personen als die Lizenz erlaubt. Gespeichert wird das trotzdem – die Übersicht
              weist darauf hin, damit ihr es beim Anbieter geradezieht.
            </div>
          ) : null}
        </div>
      )}
      </>
      )}

      {isLicense && !secretOnly ? (
        <div className="field">
          <label htmlFor="a-device">Gerät (optional)</label>
          <input
            id="a-device"
            value={device}
            onChange={(e) => setDevice(e.target.value)}
            placeholder="iMac Studio im Schnittraum"
          />
        </div>
      ) : null}

      <label className="check" style={{ margin: '16px 0 10px' }}>
        <input
          type="checkbox"
          checked={withSecret}
          onChange={(e) => setWithSecret(e.target.checked)}
        />
        {isLicense ? 'Seriennummer hier hinterlegen' : 'Eigenes Passwort hier hinterlegen'}
      </label>

      {!withSecret ? (
        <p className="small muted" style={{ marginTop: -4 }}>
          {isLicense
            ? 'Ohne Seriennummer wird nur festgehalten, wer diese Lizenz nutzt.'
            : 'Ohne Passwort wird nur festgehalten, wer einen Platz belegt – das persönliche Passwort behält die Person für sich. Für die Kostenübersicht und das Offboarding reicht das völlig.'}
        </p>
      ) : (
        <>
          {ohneSchluessel.length > 0 ? (
            <div className="note note-warn" style={{ marginBottom: 12 }}>
              {ohneSchluessel.map((u) => u.name).join(', ')} {ohneSchluessel.length === 1 ? 'hat' : 'haben'}{' '}
              noch keine Master-Passphrase gesetzt und {ohneSchluessel.length === 1 ? 'kann' : 'können'}{' '}
              deshalb nichts empfangen.
            </div>
          ) : null}

          {isLicense ? (
            <div className="field">
              <label htmlFor="a-key">Seriennummer</label>
              <div className="row-tight">
                <input
                  id="a-key"
                  type={showSecret ? 'text' : 'password'}
                  className="mono"
                  value={secret.licenseKey}
                  onChange={(e) => setSecret((s) => ({ ...s, licenseKey: e.target.value }))}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button className="btn-sm" onClick={() => setShowSecret((v) => !v)}>
                  {showSecret ? 'Verbergen' : 'Zeigen'}
                </button>
                <CopyButton value={secret.licenseKey} />
              </div>
            </div>
          ) : (
            <div className="field">
              <label htmlFor="a-pass">Passwort</label>
              <div className="row-tight">
                <input
                  id="a-pass"
                  type={showSecret ? 'text' : 'password'}
                  value={secret.password}
                  onChange={(e) => setSecret((s) => ({ ...s, password: e.target.value }))}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button className="btn-sm" onClick={() => setShowSecret((v) => !v)}>
                  {showSecret ? 'Verbergen' : 'Zeigen'}
                </button>
                <CopyButton value={secret.password} />
              </div>
              <div className="row small" style={{ marginTop: 8 }}>
                <button
                  className="btn-sm"
                  onClick={() => {
                    setSecret((s) => ({
                      ...s,
                      password: generatePassword({
                        length: 20,
                        upper: true,
                        digits: true,
                        symbols: true,
                        avoidAmbiguous: true,
                      }),
                    }));
                    setShowSecret(true);
                  }}
                >
                  Passwort erzeugen
                </button>
                <span className="spacer" />
                {secret.password ? (
                  <span className={estimateBits(secret.password) < 60 ? 'badge badge-danger' : 'badge badge-ok'}>
                    {estimateBits(secret.password)} Bit
                  </span>
                ) : null}
              </div>
            </div>
          )}

          <div className="field">
            <label htmlFor="a-notes">Notiz (optional)</label>
            <textarea
              id="a-notes"
              value={secret.notes}
              onChange={(e) => setSecret((s) => ({ ...s, notes: e.target.value }))}
              placeholder="Aktivierungsdatum, Rechnungsnummer …"
            />
          </div>

          <p className="small muted">
            {openToAll
              ? 'Lesen kann das jede Person im Team, die eine Master-Passphrase gesetzt hat.'
              : chosen.length === 0
                ? 'Lesen kannst das vorerst nur du – bis du jemanden zuweist.'
                : `Lesen können das ${chosen.map((u) => u.name).join(', ')}${
                    chosen.some((u) => u.uid === me?.uid) ? '' : ' und du'
                  }. Andere sehen lediglich, dass ${isLicense ? 'die Lizenz' : 'der Platz'} vergeben ist.`}
          </p>
        </>
      )}
    </Modal>
  );
}
